import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, tasksApi, adminApi } from '../services/api';
import Sidebar from '../components/Sidebar';
import UserAvatar from '../components/UserAvatar';
import ForceSyncButton from '../components/ForceSyncButton';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import { 
  CalendarDays, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  Users, 
  Euro,
  TrendingUp,
  Home,
  Loader2,
  ChevronRight,
  AlertTriangle,
  ArrowUpRight,
  Trash2,
  Calendar,
  MapPin,
  MoreHorizontal,
  Plus,
  Bell,
  RefreshCw,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [todayTasks, setTodayTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, tasksRes] = await Promise.all([
        dashboardApi.admin(),
        tasksApi.list({ date: format(new Date(), 'yyyy-MM-dd') })
      ]);
      setStats(dashRes.data);
      setTodayTasks(tasksRes.data);
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // fetchStats for refresh after cleanup
  const fetchStats = async () => {
    try {
      const dashRes = await dashboardApi.admin();
      setStats(dashRes.data);
    } catch (error) {
      console.error('Stats refresh error:', error);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      en_attente: { bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-400', label: 'En attente' },
      affectee: { bg: 'bg-sky-50', text: 'text-sky-600', dot: 'bg-sky-400', label: 'Affectée' },
      acceptee: { bg: 'bg-indigo-50', text: 'text-indigo-600', dot: 'bg-indigo-400', label: 'Acceptée' },
      en_cours: { bg: 'bg-violet-50', text: 'text-violet-600', dot: 'bg-violet-400', label: 'En cours' },
      termine: { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-400', label: 'Terminée' },
      probleme: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-400', label: 'Problème' },
    };
    return configs[status] || configs.en_attente;
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center md:ml-72">
          <div className="text-center">
            <div className="relative w-20 h-20 mx-auto">
              <div className="absolute inset-0 rounded-full border-4 border-[#1F3A8A]/20"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#1F3A8A] animate-spin"></div>
              <div className="absolute inset-3 rounded-full overflow-hidden bg-white shadow-lg">
                <img src="/logo-mcb.png" alt="MCB" className="w-full h-full object-cover" />
              </div>
            </div>
            <p className="mt-4 text-slate-500 font-medium">Chargement...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <Sidebar />
      
      <main className="flex-1 md:ml-72 p-4 md:p-8 pb-24 md:pb-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="pl-12 md:pl-0">
              <p className="text-slate-500 text-sm font-medium">
                {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
              </p>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mt-1">
                Bonjour, {user?.name?.split(' ')[0]} 👋
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => navigate('/admin/planning')}
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 transition-all hover:shadow-xl hover:shadow-blue-600/30 hover:-translate-y-0.5"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle tâche
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Grid - Modern Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Today's Tasks */}
          <div 
            className="group bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-slate-100 hover:border-blue-200 hover:-translate-y-1"
            onClick={() => navigate(`/admin/planning?date=${format(new Date(), 'yyyy-MM-dd')}`)}
            data-testid="today-tasks-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform">
                <CalendarDays className="h-6 w-6 text-white" />
              </div>
              <ArrowUpRight className="h-5 w-5 text-slate-300 group-hover:text-blue-500 transition-colors" />
            </div>
            <p className="text-slate-500 text-sm font-medium">Aujourd'hui</p>
            <p className="text-3xl font-bold text-slate-800 mt-1">{stats?.today_tasks || 0}</p>
            <p className="text-xs text-slate-400 mt-1">{stats?.today_completed || 0} terminées</p>
          </div>

          {/* Tomorrow */}
          <div 
            className="group bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-slate-100 hover:border-amber-200 hover:-translate-y-1"
            onClick={() => {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              navigate(`/admin/planning?date=${format(tomorrow, 'yyyy-MM-dd')}`);
            }}
            data-testid="tomorrow-tasks-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <ArrowUpRight className="h-5 w-5 text-slate-300 group-hover:text-amber-500 transition-colors" />
            </div>
            <p className="text-slate-500 text-sm font-medium">Demain</p>
            <p className="text-3xl font-bold text-slate-800 mt-1">{stats?.tomorrow_tasks || 0}</p>
            <p className="text-xs text-slate-400 mt-1">tâches prévues</p>
          </div>

          {/* Signalements */}
          <div 
            className="group bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-slate-100 hover:border-red-200 hover:-translate-y-1"
            onClick={() => navigate('/admin/signalements')}
            data-testid="signalements-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/30 group-hover:scale-110 transition-transform">
                <AlertCircle className="h-6 w-6 text-white" />
              </div>
              <ArrowUpRight className="h-5 w-5 text-slate-300 group-hover:text-red-500 transition-colors" />
            </div>
            <p className="text-slate-500 text-sm font-medium">Signalements</p>
            <p className="text-3xl font-bold text-slate-800 mt-1">{stats?.today_signalements || 0}</p>
            <p className="text-xs text-slate-400 mt-1">{stats?.pending_signalements || 0} en attente</p>
          </div>

          {/* Revenue */}
          <div 
            className="group bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-slate-100 hover:border-emerald-200 hover:-translate-y-1"
            onClick={() => navigate('/admin/payments')}
            data-testid="revenue-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform">
                <Euro className="h-6 w-6 text-white" />
              </div>
              <ArrowUpRight className="h-5 w-5 text-slate-300 group-hover:text-emerald-500 transition-colors" />
            </div>
            <p className="text-slate-500 text-sm font-medium">À payer</p>
            <p className="text-3xl font-bold text-slate-800 mt-1">{stats?.total_unpaid || 0}€</p>
            <p className="text-xs text-slate-400 mt-1">{stats?.completed_unpaid || 0} prestations</p>
          </div>
        </div>

        {/* Alerte prestations en retard */}
        {stats?.overdue_count > 0 && (
          <div 
            className="bg-red-50 border-2 border-red-200 rounded-xl p-4"
            data-testid="overdue-alert"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-red-700">
                    {stats.overdue_count} prestation{stats.overdue_count > 1 ? 's' : ''} en retard
                  </h3>
                  <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                    ACTION REQUISE
                  </span>
                </div>
              </div>
            </div>
            {/* Liste détaillée des tâches en retard */}
            <div className="space-y-2 ml-13">
              {stats.overdue_tasks?.map((task, index) => (
                <div 
                  key={task.id || index}
                  onClick={() => navigate(`/admin/tasks/${task.id}`)}
                  className="flex items-center gap-3 p-2 bg-white rounded-lg border border-red-100 hover:bg-red-50 cursor-pointer transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{task.property_name}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {task.housekeeper_name || 'Non assignée'}
                      </span>
                      <span>•</span>
                      <span className="text-red-600 font-medium">{task.date}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-red-400 shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Today's Tasks List */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Tâches du jour</h2>
                  <p className="text-xs text-slate-400">{todayTasks.length} mission{todayTasks.length > 1 ? 's' : ''}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin/planning')}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                Voir tout
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            <div className="divide-y divide-slate-50">
              {todayTasks.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="h-8 w-8 text-slate-300" />
                  </div>
                  <p className="text-slate-500 font-medium">Aucune tâche aujourd'hui</p>
                  <p className="text-slate-400 text-sm mt-1">Profitez de cette journée calme !</p>
                </div>
              ) : (
                todayTasks.slice(0, 5).map((task, index) => {
                  const config = getStatusConfig(task.status);
                  return (
                    <div 
                      key={task.id}
                      onClick={() => navigate(`/admin/tasks/${task.id}`)}
                      className="p-4 hover:bg-slate-50/50 cursor-pointer transition-colors group"
                      style={{ animationDelay: `${index * 50}ms` }}
                      data-testid={`task-item-${task.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-lg"
                          style={{ backgroundColor: task.property_color || '#3B82F6' }}
                        >
                          {task.property_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-800 truncate">{task.property_name}</p>
                            {task.is_urgent && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full">
                                URGENT
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                            {task.housekeeper_name && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3.5 w-3.5" />
                                {task.housekeeper_name}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Euro className="h-3.5 w-3.5" />
                              {task.price || 0}€
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${config.bg}`}>
                            <div className={`w-2 h-2 rounded-full ${config.dot}`}></div>
                            <span className={`text-xs font-medium ${config.text}`}>{config.label}</span>
                          </div>
                          <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl p-6 text-white shadow-xl shadow-blue-600/20">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">CA ce mois</h3>
                  <p className="text-blue-200 text-xs">Prestations terminées</p>
                </div>
              </div>
              <p className="text-4xl font-bold mb-1">{stats?.month_revenue || 0}€</p>
              <p className="text-blue-200 text-sm">{stats?.month_completed || 0} missions terminées</p>
            </div>

            {/* Team Overview */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Users className="h-5 w-5 text-slate-400" />
                  Équipe active
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/admin/users')}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-3">
                {stats?.active_housekeepers?.slice(0, 3).map((hk, i) => (
                  <div key={hk.id || i} className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors">
                    <UserAvatar 
                      user={hk} 
                      size="md"
                      className="w-10 h-10"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-700 text-sm truncate">{hk.name}</p>
                      <p className="text-xs text-slate-400">{hk.tasks_today || 0} tâche{(hk.tasks_today || 0) > 1 ? 's' : ''} aujourd'hui</p>
                    </div>
                    <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                  </div>
                )) || (
                  <p className="text-slate-400 text-sm text-center py-4">Aucune équipe configurée</p>
                )}
              </div>
            </div>

            {/* Alerts */}
            {stats?.problem_tasks > 0 && (
              <div 
                className="bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl p-5 text-white cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 shadow-lg shadow-red-500/20"
                onClick={() => navigate('/admin/planning')}
                data-testid="problems-alert"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur">
                    <AlertTriangle className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-bold text-lg">{stats.problem_tasks} problème{stats.problem_tasks > 1 ? 's' : ''}</p>
                    <p className="text-red-100 text-sm">Cliquez pour voir les détails</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Admin Tools - Bottom */}
        <div className="mt-8 pt-6 border-t border-slate-200">
          {/* Force Sync Button - Plus visible */}
          <div className="mb-6">
            <ForceSyncButton variant="full" />
          </div>
          
          <div className="flex flex-wrap gap-4 items-center">
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`${API_URL}/api/admin/cleanup-orphan-payments`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  if (!res.ok) {
                    const errorData = await res.json().catch(() => ({}));
                    throw new Error(errorData.detail || `Erreur ${res.status}`);
                  }
                  const data = await res.json();
                  if (data.deleted_count > 0) {
                    toast.success(`${data.deleted_count} paiement(s) orphelin(s) supprimé(s)`);
                    fetchStats(); // Refresh stats
                  } else {
                    toast.info('Aucun paiement orphelin trouvé');
                  }
                } catch (error) {
                  console.error('Cleanup error:', error);
                  toast.error(`Erreur: ${error.message || 'Impossible de nettoyer les paiements'}`);
                }
              }}
              className="text-xs text-emerald-600 hover:text-emerald-700 transition-colors flex items-center gap-1 bg-emerald-50 px-3 py-2 rounded-lg"
            >
              <RefreshCw className="h-3 w-3" />
              Nettoyer paiements orphelins
            </button>
            
            <button
              onClick={async () => {
                if (!window.confirm('⚠️ Ceci va supprimer TOUS les "À percevoir" de toutes les FDM. Continuer ?')) {
                  return;
                }
                try {
                  const res = await fetch(`${API_URL}/api/admin/reset-all-pending-payments`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  if (!res.ok) {
                    const errorData = await res.json().catch(() => ({}));
                    throw new Error(errorData.detail || `Erreur ${res.status}`);
                  }
                  const data = await res.json();
                  toast.success(`${data.deleted_count} paiement(s) en attente supprimé(s)`);
                  fetchStats();
                } catch (error) {
                  console.error('Reset error:', error);
                  toast.error(`Erreur: ${error.message}`);
                }
              }}
              className="text-xs text-red-600 hover:text-red-700 transition-colors flex items-center gap-1 bg-red-50 px-3 py-2 rounded-lg"
            >
              <RefreshCw className="h-3 w-3" />
              RAZ tous les "À percevoir"
            </button>
            
            <button
              onClick={async () => {
                if (!window.confirm('⚠️ Ceci va supprimer TOUTES les factures reçues. Les FDM devront renvoyer leurs vraies factures. Continuer ?')) {
                  return;
                }
                try {
                  const res = await fetch(`${API_URL}/api/admin/cleanup-invoices`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  if (!res.ok) {
                    const errorData = await res.json().catch(() => ({}));
                    throw new Error(errorData.detail || `Erreur ${res.status}`);
                  }
                  const data = await res.json();
                  toast.success(`${data.deleted_count} facture(s) supprimée(s)`);
                } catch (error) {
                  console.error('Cleanup invoices error:', error);
                  toast.error(`Erreur: ${error.message}`);
                }
              }}
              className="text-xs text-orange-600 hover:text-orange-700 transition-colors flex items-center gap-1 bg-orange-50 px-3 py-2 rounded-lg"
            >
              <RefreshCw className="h-3 w-3" />
              RAZ Factures reçues
            </button>
            
            <button
              onClick={async () => {
                if (!window.confirm('⚠️ Ceci va supprimer TOUTES les factures auto-entrepreneur générées par les FDM.\n\nLes FDM pourront en générer de nouvelles à partir du 1er du mois suivant.\n\nContinuer ?')) {
                  return;
                }
                try {
                  const res = await fetch(`${API_URL}/api/admin/reset-auto-invoices`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  if (!res.ok) {
                    const errorData = await res.json().catch(() => ({}));
                    throw new Error(errorData.detail || `Erreur ${res.status}`);
                  }
                  const data = await res.json();
                  toast.success(`${data.deleted_count} facture(s) auto-entrepreneur supprimée(s)`);
                } catch (error) {
                  console.error('Reset auto-invoices error:', error);
                  toast.error(`Erreur: ${error.message}`);
                }
              }}
              className="text-xs text-red-600 hover:text-red-700 transition-colors flex items-center gap-1 bg-red-50 px-3 py-2 rounded-lg"
              data-testid="reset-auto-invoices-btn"
            >
              <RefreshCw className="h-3 w-3" />
              RAZ Factures FDM
            </button>
            
            <button
              onClick={async () => {
                try {
                  toast.info('Envoi du mail de test...');
                  const res = await fetch(`${API_URL}/api/admin/test-email`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  const data = await res.json();
                  if (data.success) {
                    toast.success(`✅ Email envoyé ! Vérifiez votre boîte de réception.`);
                  } else {
                    toast.error(`❌ Erreur: ${data.error || 'Échec envoi'}`);
                  }
                } catch (error) {
                  console.error('Test email error:', error);
                  toast.error(`Erreur: ${error.message}`);
                }
              }}
              className="text-xs text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-1 bg-blue-50 px-3 py-2 rounded-lg"
              data-testid="test-email-btn"
            >
              <Mail className="h-3 w-3" />
              Tester les emails
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
